/**
 * Anmeldung, Pflichtschritte und eigenes Konto (Spec 8.2a, 8.2b).
 *
 * Maßstab ist wie überall jemand, der zum ersten Mal davorsitzt: Beim ersten
 * Anmelden mit dem Einmalpasswort führt die App durch genau zwei Schritte —
 * eigenes Passwort, dann (für Buchhaltung und Administration) der zweite
 * Faktor. Jeder Schritt sagt, warum er da ist.
 */
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "../lib/api";
import type { MeDto, MeUser } from "../lib/auth";
import { ME_KEY, applySession } from "../lib/session";
import { ErrorBox, PageHeader, Section } from "../components/ui";
import { NivaroLogo } from "../components/NivaroLogo";

const MIN_LENGTH = 10;

/** Wer angemeldet ist. Eine Abfrage für die ganze App, gehalten bis zum Abmelden. */
export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: () => api.get<MeDto>("/auth/me"),
    staleTime: Infinity,
    retry: 1,
  });
}

/** Nach dem Anmelden: nichts vom vorigen Zugang im Speicher lassen. */
function useApplyMe() {
  const queryClient = useQueryClient();
  return (me: MeDto, fresh = false) => applySession(queryClient, me, fresh);
}

export function useLogout(mode: MeDto["mode"]) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/auth/logout"),
    onSettled: () => applySession(queryClient, { mode, user: null }, true),
  });
}

/** Ruhiger Rahmen für alles, was vor der eigentlichen App kommt. */
export function AuthFrame({ children, footer }: { children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-4">
          <NivaroLogo className="h-9" />
          <span className="h-7 w-px bg-line" aria-hidden />
          <span className="font-display text-2xl font-medium tracking-tight">
            Trinkgeld<span className="text-accent">.</span>
          </span>
        </div>
        <div className="card p-8">{children}</div>
        {footer ? <div className="mt-4 text-center text-xs text-muted">{footer}</div> : null}
      </div>
    </div>
  );
}

// --- Anmelden -----------------------------------------------------------------

export function Anmeldung({ mailReset, microsoft }: { mailReset: boolean; microsoft: boolean }) {
  const [vergessen, setVergessen] = useState(false);
  if (vergessen) return <PasswortVergessen mailReset={mailReset} onBack={() => setVergessen(false)} />;
  return <AnmeldeFormular microsoft={microsoft} onVergessen={() => setVergessen(true)} />;
}

/**
 * Was nach einem Rückruf von Microsoft schiefging. Die API schickt nur einen
 * kurzen Grund in der Adresse (`?anmeldung=`), nie Einzelheiten — die stehen
 * im Server-Log.
 */
const MICROSOFT_FEHLER: Record<string, string> = {
  microsoft_wartet:
    "Ihre Anmeldung ist angekommen. Die Administration muss Ihr Microsoft-Konto einmal freigeben — danach melden Sie sich hier wieder mit Microsoft an.",
  microsoft_deaktiviert: "Ihr Zugang ist deaktiviert. Die Administration kann ihn wieder aktivieren.",
  microsoft_abgelaufen:
    "Die Anmeldung hat zu lange gedauert oder wurde in einem anderen Fenster begonnen. Bitte noch einmal.",
  microsoft_abgebrochen: "Die Anmeldung bei Microsoft wurde abgebrochen.",
  microsoft_fehler:
    "Microsoft hat die Anmeldung nicht bestätigt. Bitte noch einmal; bleibt es dabei, steht der Grund im Server-Log.",
  microsoft_aus: "Die Anmeldung mit Microsoft ist auf diesem Server nicht eingerichtet.",
};

function useMicrosoftFehler(): string | null {
  const [fehler] = useState(() => {
    const code = new URLSearchParams(window.location.search).get("anmeldung");
    return code ? (MICROSOFT_FEHLER[code] ?? MICROSOFT_FEHLER["microsoft_fehler"] ?? null) : null;
  });
  useEffect(() => {
    // Der Grund steht nur einmal da; neu laden zeigt eine saubere Seite.
    if (window.location.search.includes("anmeldung=")) {
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);
  return fehler;
}

function AnmeldeFormular({ microsoft, onVergessen }: { microsoft: boolean; onVergessen: () => void }) {
  const applyMe = useApplyMe();
  const microsoftFehler = useMicrosoftFehler();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [needCode, setNeedCode] = useState(false);

  const login = useMutation({
    mutationFn: () =>
      api.post<MeDto>("/auth/login", { username, password, ...(needCode ? { code } : {}) }),
    onSuccess: (me) => applyMe(me, true),
    onError: (err) => {
      if (err instanceof ApiError && err.code === "totp_required") setNeedCode(true);
      if (err instanceof ApiError && err.code === "totp_invalid") setCode("");
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate();
  };

  // „Code fehlt" ist kein Fehler, sondern der zweite Schritt.
  const showError =
    login.error && !(login.error instanceof ApiError && login.error.code === "totp_required");

  return (
    <AuthFrame
      footer={
        <button type="button" className="underline hover:text-ink" onClick={onVergessen}>
          Passwort vergessen?
        </button>
      }
    >
      <div className="label mb-2">· Anmeldung</div>
      <h1 className="mb-6 text-xl">Trinkgeldabrechnung Bhf. Ehrenfeld</h1>
      {microsoftFehler ? (
        <div className="mb-4">
          {/* „Wartet auf Freigabe“ ist kein Fehler, sondern der vorgesehene erste Schritt. */}
          {microsoftFehler === MICROSOFT_FEHLER["microsoft_wartet"] ? (
            <p className="rounded border border-accent/30 bg-accent/5 px-4 py-3 text-sm">{microsoftFehler}</p>
          ) : (
            <ErrorBox error={new Error(microsoftFehler)} />
          )}
        </div>
      ) : null}
      {microsoft && !needCode ? (
        <>
          {/* Ein Link, kein Knopf mit fetch: die API leitet zu Microsoft weiter,
              und das muss eine ganz normale Navigation sein. */}
          <a href="/api/v1/auth/microsoft/start" className="btn-primary w-full justify-center">
            Mit Microsoft anmelden
          </a>
          <div className="my-5 flex items-center gap-3 text-xs text-muted">
            <span className="h-px flex-1 bg-line" />
            oder mit Anmeldename und Passwort
            <span className="h-px flex-1 bg-line" />
          </div>
        </>
      ) : null}
      <form onSubmit={submit} className="space-y-4">
        <label className="block">
          <span className="label mb-1 block">Anmeldename</span>
          <input
            className="field"
            autoComplete="username"
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={needCode}
          />
        </label>
        <label className="block">
          <span className="label mb-1 block">Passwort</span>
          <input
            className="field"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={needCode}
          />
        </label>

        {needCode ? (
          <label className="block">
            <span className="label mb-1 block">Code aus der Authenticator-App</span>
            <input
              className="field font-mono tracking-[0.3em]"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={7}
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, ""))}
            />
            <span className="mt-1 block text-xs text-muted">
              Sechs Ziffern, erneuern sich alle 30 Sekunden. Es gilt der gerade angezeigte.
            </span>
          </label>
        ) : null}

        {showError ? <ErrorBox error={login.error} /> : null}
        {showError && microsoft && login.error instanceof ApiError && login.error.code === "login_failed" ? (
          <p className="text-xs text-muted">
            Die meisten Zugänge haben kein Passwort und melden sich nur mit Microsoft an — dann den
            Knopf oben nehmen.
          </p>
        ) : null}

        <div className="flex items-center gap-3 pt-2">
          <button
            className={microsoft && !needCode ? "btn-outline" : "btn-primary"}
            type="submit"
            disabled={login.isPending || !username || !password || (needCode && code.replace(/\s/g, "").length !== 6)}
          >
            {login.isPending ? "Prüft …" : "Anmelden"}
          </button>
          {needCode ? (
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                setNeedCode(false);
                setCode("");
                setPassword("");
                login.reset();
              }}
            >
              Anders anmelden
            </button>
          ) : null}
        </div>
      </form>
    </AuthFrame>
  );
}

// --- Passwort vergessen ------------------------------------------------------------

function PasswortVergessen({ mailReset, onBack }: { mailReset: boolean; onBack: () => void }) {
  const [identifier, setIdentifier] = useState("");
  const request = useMutation({
    mutationFn: () => api.post<{ ok: true; message: string }>("/auth/password-forgot", { identifier }),
  });

  return (
    <AuthFrame
      footer={
        <button type="button" className="underline hover:text-ink" onClick={onBack}>
          Zurück zur Anmeldung
        </button>
      }
    >
      <div className="label mb-2">· Passwort vergessen</div>
      <h1 className="mb-4 text-xl">Neues Passwort festlegen</h1>
      {!mailReset ? (
        <p className="text-sm text-muted">
          Die Administration setzt Ihr Passwort zurück und gibt Ihnen ein Einmalpasswort. Damit
          melden Sie sich an und legen gleich danach ein eigenes fest.
        </p>
      ) : request.isSuccess ? (
        <div className="space-y-3 text-sm">
          <p>{request.data.message}</p>
          <p className="text-muted">
            Keine Mail bekommen? Auch im Spam-Ordner nachsehen. Ist keine E-Mail-Adresse
            hinterlegt, hilft die Administration.
          </p>
        </div>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            request.mutate();
          }}
        >
          <p className="text-sm text-muted">
            Wir schicken einen Link an die E-Mail-Adresse, die für Ihren Zugang hinterlegt ist.
          </p>
          <label className="block">
            <span className="label mb-1 block">Anmeldename oder E-Mail</span>
            <input
              className="field"
              autoComplete="username"
              autoFocus
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
            />
          </label>
          {request.error ? <ErrorBox error={request.error} /> : null}
          <button className="btn-primary" type="submit" disabled={request.isPending || !identifier.trim()}>
            {request.isPending ? "Sendet …" : "Link schicken"}
          </button>
        </form>
      )}
    </AuthFrame>
  );
}

/**
 * Ziel des Links aus der Mail. Das Token steht im Fragment (`#t=…`), das der
 * Browser nie an einen Server schickt. Gleich nach dem Lesen verschwindet es
 * auch aus der Adresszeile und dem Verlauf.
 */
export function PasswortNeu() {
  const queryClient = useQueryClient();
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get("t") ?? "");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");

  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, "", window.location.pathname);
  }, []);

  const check = useQuery({
    queryKey: ["reset-check", token],
    enabled: token !== "",
    retry: false,
    staleTime: Infinity,
    queryFn: () =>
      api.post<{ ok: true; username: string; displayName: string } | { ok: false; message: string }>(
        "/auth/password-reset/check",
        { token },
      ),
  });
  const save = useMutation({
    mutationFn: () => api.post<{ ok: true }>("/auth/password-reset", { token, next }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ME_KEY }),
  });

  const zurAnmeldung = (
    <a href="/" className="btn-primary inline-flex">
      Zur Anmeldung
    </a>
  );

  let inhalt: ReactNode;
  if (!token) {
    inhalt = <ErrorBox error={new Error("Der Link ist unvollständig. Bitte den ganzen Link aus der Mail öffnen.")} />;
  } else if (check.isLoading) {
    inhalt = <p className="text-sm text-muted">Prüft den Link …</p>;
  } else if (check.error) {
    inhalt = <ErrorBox error={check.error} />;
  } else if (check.data && !check.data.ok) {
    inhalt = (
      <div className="space-y-4">
        <ErrorBox error={new Error(check.data.message)} />
        {zurAnmeldung}
      </div>
    );
  } else if (save.isSuccess) {
    inhalt = (
      <div className="space-y-4 text-sm">
        <p>
          Gespeichert. Alle bisherigen Anmeldungen sind beendet. Melden Sie sich jetzt mit dem
          neuen Passwort an.
        </p>
        {zurAnmeldung}
      </div>
    );
  } else if (check.data?.ok) {
    const tooShort = next.length > 0 && next.length < MIN_LENGTH;
    const mismatch = repeat.length > 0 && next !== repeat;
    inhalt = (
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <p className="text-sm text-muted">
          Für {check.data.displayName} ({check.data.username}).
        </p>
        {/* Für Passwortmanager: zu welchem Konto das neue Passwort gehört. */}
        <input type="text" autoComplete="username" value={check.data.username} readOnly hidden />
        <label className="block">
          <span className="label mb-1 block">Neues Passwort</span>
          <input
            className="field"
            type="password"
            autoComplete="new-password"
            autoFocus
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
          <span className={`mt-1 block text-xs ${tooShort ? "text-alert" : "text-muted"}`}>
            Mindestens {MIN_LENGTH} Zeichen. Ein kurzer Satz ist sicherer als „Sommer2026!".
          </span>
        </label>
        <label className="block">
          <span className="label mb-1 block">Neues Passwort wiederholen</span>
          <input
            className="field"
            type="password"
            autoComplete="new-password"
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
          />
          {mismatch ? <span className="mt-1 block text-xs text-alert">Stimmt nicht überein.</span> : null}
        </label>
        {save.error ? <ErrorBox error={save.error} /> : null}
        <button
          className="btn-primary"
          type="submit"
          disabled={save.isPending || next.length < MIN_LENGTH || next !== repeat}
        >
          {save.isPending ? "Speichert …" : "Passwort festlegen"}
        </button>
      </form>
    );
  }

  return (
    <AuthFrame>
      <div className="label mb-2">· Passwort festlegen</div>
      <h1 className="mb-4 text-xl">Neues Passwort</h1>
      {inhalt}
    </AuthFrame>
  );
}

// --- Passwort ändern ------------------------------------------------------------

export function PasswortAendern({ erzwungen, onDone }: { erzwungen: boolean; onDone?: () => void }) {
  const applyMe = useApplyMe();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [done, setDone] = useState(false);

  const save = useMutation({
    mutationFn: () => api.post<MeDto>("/auth/password", { current, next }),
    onSuccess: (me) => {
      setCurrent("");
      setNext("");
      setRepeat("");
      setDone(true);
      applyMe(me);
      onDone?.();
    },
  });

  const tooShort = next.length > 0 && next.length < MIN_LENGTH;
  const mismatch = repeat.length > 0 && next !== repeat;

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      {erzwungen ? (
        <p className="text-sm text-muted">
          Das Passwort, mit dem Sie sich gerade angemeldet haben, war ein Einmalpasswort. Legen Sie
          jetzt ein eigenes fest, das nur Sie kennen.
        </p>
      ) : null}
      <label className="block">
        <span className="label mb-1 block">{erzwungen ? "Einmalpasswort" : "Bisheriges Passwort"}</span>
        <input
          className="field"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
      </label>
      <label className="block">
        <span className="label mb-1 block">Neues Passwort</span>
        <input
          className="field"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => {
            setNext(e.target.value);
            setDone(false);
          }}
        />
        <span className={`mt-1 block text-xs ${tooShort ? "text-alert" : "text-muted"}`}>
          Mindestens {MIN_LENGTH} Zeichen. Länge schlägt Sonderzeichen: ein kurzer Satz ist
          sicherer als „Sommer2026!".
        </span>
      </label>
      <label className="block">
        <span className="label mb-1 block">Neues Passwort wiederholen</span>
        <input
          className="field"
          type="password"
          autoComplete="new-password"
          value={repeat}
          onChange={(e) => setRepeat(e.target.value)}
        />
        {mismatch ? <span className="mt-1 block text-xs text-alert">Stimmt nicht überein.</span> : null}
      </label>

      {save.error ? <ErrorBox error={save.error} /> : null}
      {done && !erzwungen ? (
        <p className="text-sm text-ok">Gespeichert. Andere Anmeldungen mit dem alten Passwort sind beendet.</p>
      ) : null}

      <button
        className="btn-primary"
        type="submit"
        disabled={save.isPending || !current || next.length < MIN_LENGTH || next !== repeat}
      >
        {save.isPending ? "Speichert …" : "Passwort festlegen"}
      </button>
    </form>
  );
}

// --- Zweiter Faktor --------------------------------------------------------------

interface TotpSetup {
  secret: string;
  uri: string;
  qrSvg: string;
}

export function ZweiFaktorEinrichten({ erzwungen }: { erzwungen: boolean }) {
  const applyMe = useApplyMe();
  const [code, setCode] = useState("");

  // Bewusst per Knopf, nicht beim Anzeigen: jeder Start erzeugt ein neues
  // Geheimnis, und ein doppelt ausgelöster Start zeigte einen QR-Code, der
  // schon nicht mehr gilt.
  const start = useMutation({ mutationFn: () => api.post<TotpSetup>("/auth/totp/setup") });
  const confirm = useMutation({
    mutationFn: () => api.post<MeDto>("/auth/totp/confirm", { code }),
    onSuccess: (me) => applyMe(me),
    onError: () => setCode(""),
  });

  const setup = start.data;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted">
        {erzwungen
          ? "Wer IBANs sehen und Auszahlungen freigeben darf, meldet sich mit einem zweiten Faktor an: "
          : "Zusätzlich zum Passwort: "}
        einem Code aus einer Authenticator-App auf dem Handy. Ein erratenes oder mitgelesenes
        Passwort allein reicht dann nicht mehr.
      </p>

      {!setup ? (
        <>
          <p className="text-sm text-muted">
            Sie brauchen eine Authenticator-App, zum Beispiel Microsoft Authenticator, Google
            Authenticator oder die in 1Password. Ist sie installiert, geht es los.
          </p>
          {start.error ? <ErrorBox error={start.error} /> : null}
          <button className="btn-primary" onClick={() => start.mutate()} disabled={start.isPending}>
            {start.isPending ? "Erzeugt …" : "Einrichtung starten"}
          </button>
        </>
      ) : (
        <>
          <ol className="list-decimal space-y-1 pl-5 text-sm">
            <li>In der App „Konto hinzufügen“ wählen und diesen Code scannen.</li>
            <li>Die App zeigt danach alle 30 Sekunden sechs neue Ziffern.</li>
            <li>Die gerade angezeigten hier eingeben.</li>
          </ol>
          <div className="flex justify-center rounded border border-line bg-white p-4">
            <img
              src={`data:image/svg+xml;utf8,${encodeURIComponent(setup.qrSvg)}`}
              alt="QR-Code für die Authenticator-App"
              className="h-48 w-48"
            />
          </div>
          <details className="text-xs text-muted">
            <summary className="cursor-pointer">Scannen geht nicht? Schlüssel zum Abtippen</summary>
            <div className="mt-2 break-all font-mono text-sm text-ink">{setup.secret}</div>
            <div className="mt-1">Zeitbasiert, 6 Ziffern, 30 Sekunden.</div>
          </details>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              confirm.mutate();
            }}
          >
            <label className="block">
              <span className="label mb-1 block">Code aus der App</span>
              <input
                className="field font-mono tracking-[0.3em]"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                maxLength={7}
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, ""))}
              />
            </label>
            {confirm.error ? <ErrorBox error={confirm.error} /> : null}
            <button
              className="btn-primary"
              type="submit"
              disabled={confirm.isPending || code.replace(/\s/g, "").length !== 6}
            >
              {confirm.isPending ? "Prüft …" : "Bestätigen"}
            </button>
          </form>
        </>
      )}
    </div>
  );
}

// --- Pflichtschritt vor der App ---------------------------------------------------

export function Pflichtschritt({ me }: { me: MeDto & { user: MeUser } }) {
  const logout = useLogout(me.mode);
  const step = me.user.pending;
  const viaMicrosoft = me.user.loginMethod === "MICROSOFT";
  const label =
    step === "PASSWORD_CHANGE"
      ? "Schritt 1: Eigenes Passwort"
      : viaMicrosoft
        ? "Zweiter Faktor"
        : "Schritt 2: Zweiter Faktor";
  const titel =
    step === "PASSWORD_CHANGE"
      ? "Eigenes Passwort festlegen"
      : step === "TOTP_VERIFY"
        ? "Code aus der Authenticator-App"
        : "Zweiten Faktor einrichten";
  return (
    <AuthFrame
      footer={
        <button className="underline hover:text-ink" onClick={() => logout.mutate()}>
          Abmelden
        </button>
      }
    >
      <div className="label mb-2">· {label}</div>
      <h1 className="mb-6 text-xl">{titel}</h1>
      {step === "PASSWORD_CHANGE" ? (
        <PasswortAendern erzwungen />
      ) : step === "TOTP_VERIFY" ? (
        <CodeBestaetigen />
      ) : (
        <ZweiFaktorEinrichten erzwungen />
      )}
    </AuthFrame>
  );
}

/**
 * Nach „Mit Microsoft anmelden", wenn die App ihren eigenen Code verlangt:
 * Microsoft hat bestätigt, wer es ist; für die Bankdaten kommt der zweite
 * Faktor aus der App dazu.
 */
function CodeBestaetigen() {
  const applyMe = useApplyMe();
  const [code, setCode] = useState("");
  const verify = useMutation({
    mutationFn: () => api.post<MeDto>("/auth/totp/verify", { code }),
    onSuccess: (me) => applyMe(me),
    onError: () => setCode(""),
  });
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        verify.mutate();
      }}
    >
      <p className="text-sm text-muted">
        Microsoft hat Sie angemeldet. Für den Zugang zu den Bankdaten fragt die App zusätzlich
        den Code aus Ihrer Authenticator-App ab.
      </p>
      <label className="block">
        <span className="label mb-1 block">Code</span>
        <input
          className="field font-mono tracking-[0.3em]"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          maxLength={7}
          placeholder="123456"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, ""))}
        />
      </label>
      {verify.error ? <ErrorBox error={verify.error} /> : null}
      <button
        className="btn-primary"
        type="submit"
        disabled={verify.isPending || code.replace(/\s/g, "").length !== 6}
      >
        {verify.isPending ? "Prüft …" : "Bestätigen"}
      </button>
    </form>
  );
}

// --- Mein Konto -------------------------------------------------------------------

export function Konto({ me }: { me: MeDto & { user: MeUser } }) {
  const { user, mode } = me;
  return (
    <>
      <PageHeader label="Konto" title={user.displayName ?? "Mein Konto"}>
        {user.roleLabel ? `Rolle: ${user.roleLabel}` : null}
        {user.username ? ` · Anmeldename: ${user.username}` : null}
      </PageHeader>

      {mode !== "local" ? (
        <p className="text-sm text-muted">
          Im Entwicklungsmodus gibt es keine Anmeldung und damit nichts zu verwalten.
        </p>
      ) : (
        <div className="grid gap-10 lg:grid-cols-2">
          <Section label="Sicherheit" title="Passwort ändern">
            <div className="card max-w-md p-6">
              {user.passwordAccess ? (
                <PasswortAendern erzwungen={false} />
              ) : (
                <p className="text-sm text-muted">
                  Ihr Zugang hat kein Passwort: Sie melden sich mit Microsoft an. Wer bei Microsoft
                  gesperrt wird, kommt so auch hier nicht mehr herein. Einen Passwortzugang als
                  Notfallweg richtet die Administration ein.
                </p>
              )}
            </div>
          </Section>
          <Section label="Sicherheit" title="Zweiter Faktor">
            <div className="card max-w-md p-6">
              {user.totpEnabled ? (
                <p className="text-sm">
                  <span className="chip-ok mr-2">Eingerichtet</span>
                  Bei jeder Anmeldung wird zusätzlich der Code aus der App verlangt. Handy verloren
                  oder gewechselt? Die Administration setzt den zweiten Faktor zurück; beim nächsten
                  Anmelden richten Sie ihn neu ein.
                </p>
              ) : (
                <ZweiFaktorEinrichten erzwungen={false} />
              )}
            </div>
          </Section>
        </div>
      )}
    </>
  );
}
